import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

interface PinVerifyDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onVerified: (token: string) => void;
}

export default function PinVerifyDialog({ open, onOpenChange, onVerified }: PinVerifyDialogProps) {
    const { verifyPin } = useAuth();
    const [pin, setPin] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setPin('');
            setLoading(false);
        }
    }, [open]);

    const handleVerify = async () => {
        if (pin.length !== 4) return;
        setLoading(true);
        try {
            const pinToken = await verifyPin(pin);
            onVerified(pinToken);
            onOpenChange(false);
        } catch (e: any) {
            toast.error(e.response?.data?.detail || 'Invalid PIN');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (!loading && pin.length === 4) {
                handleVerify();
            }
        }
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!loading && pin.length === 4) {
            handleVerify();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>PIN Verification Required</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <div className="space-y-2 py-4">
                        <Label htmlFor="pin">Enter your 4-digit PIN</Label>
                        <Input
                            id="pin"
                            type="password"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={4}
                            value={pin}
                            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            onKeyDown={handleKeyDown}
                            placeholder="0000"
                            data-testid="pin-verify-input"
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading} data-testid="pin-verify-cancel">
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading || pin.length !== 4} data-testid="pin-verify-submit">
                            Verify
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
